import { QueryList, ViewChildren, Directive } from "@angular/core";
import { AbstractControl, NgControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { FormRequest, FormResponse, FormSaveResponse } from '../../../../modules/tcm-core.module';
import { Subscription, Observable } from "rxjs";
import { EntityPermissionNode, SpecialActionAccesses, SpecialActionNode } from "../../../../models/roles";
import { SecureBaseComponentContext } from "../../../../modules/tcm-common/services/secure-base-component-context.service";
import { PermissionsConsumer } from "../../../../components/permissions-consumer";
import { PermissionsInfo } from "../../../../components/permissions-info";
import { TcmBaseDetails } from "../../base/tcm-base-details";
import { ActionButtonDirective } from "../../directives/action-button.directive";
import { BaseSecureLinkedCollection } from "../base-secure-linked-collection/base-secure-linked-collection";
import { NullObjectSpecialActions } from '../null-object-special-actions';
import { FormControlBase } from "../../../../modules/tcm-form/common/form-control-base/form-control-base";


@Directive()
export class BaseSecureDetails<TFormRequest extends FormRequest, TFormResponse extends FormResponse<any>, TFormSaveResponse extends FormSaveResponse = FormSaveResponse> extends TcmBaseDetails<TFormRequest, TFormResponse, TFormSaveResponse> implements PermissionsConsumer {

    @ViewChildren(BaseSecureLinkedCollection)
    private linkedCollections: QueryList<BaseSecureLinkedCollection<any>>;

    @ViewChildren(NgControl)
    private controls: QueryList<NgControl>;

    @ViewChildren(ActionButtonDirective)
    private buttons: QueryList<ActionButtonDirective>;

    private entityPermissions: EntityPermissionNode;

    private permissionsSubscription: Subscription;
    private controlsSubscription: Subscription;
    private buttonsSubscription: Subscription;
    private _permissionsInfo: PermissionsInfo;

    get canEdit() {
        return this.entityPermissions && this.entityPermissions.canEdit && this.checkAdditionalCanEditRules();
    }

    get specialActionPermissions(): SpecialActionAccesses {
        return this.componentContext.permissionService.currentPermissions
            ? this.componentContext.permissionService.currentPermissions.specialActions
            : NullObjectSpecialActions.specialActions;
    }

    set permissionsInfo(permissionsInfo: PermissionsInfo) {
        if (!this._permissionsInfo) {
            this._permissionsInfo = permissionsInfo;
            this.setPermissionsInfo(permissionsInfo);
        }
    }

    constructor(protected override componentContext: SecureBaseComponentContext, protected override route: ActivatedRoute) {
        super(componentContext, route);
    }

    override ngAfterViewInit() {
        super.ngAfterViewInit();

        this.permissionsInfo = this.route.snapshot.data as PermissionsInfo;

        this.controlsSubscription = this.controls.changes.subscribe(() => { this.toggleControlsReadonly(); });
        this.buttonsSubscription = this.buttons.changes.subscribe(() => this.toggleButtonsReadonly());
        this.linkedCollections.changes.subscribe(() => {
            this.linkedCollections.forEach(x => {
                x.editable = this.canEdit;
            });
        });
    }

    override ngOnDestroy() {
        super.ngOnDestroy();

        if (this.permissionsSubscription) {
            this.permissionsSubscription.unsubscribe();
        }

        this.controlsSubscription.unsubscribe();
        this.buttonsSubscription.unsubscribe();
    }

    protected override onViewModelChanged() {
        super.onViewModelChanged();

        this.setScreenReadonlyIfNeeded();
    }

    protected setScreenReadonlyIfNeeded() {
        this.toggleControlsReadonly();
        this.toggleButtonsReadonly();
    }

    protected checkCanEditRuleByControl(control: ActionButtonDirective | FormControlBase<any>) {
        return null;
    }

    protected checkAdditionalCanEditRules() {
        return true;
    }

    protected onPermissionsUpdated() {
    }

    protected getScreenEntityPermission$(areaPermissionKey: string,  entityPermissionKey: string) : Observable<EntityPermissionNode> {
        return this.componentContext.permissionService.getEntityPermissions$(areaPermissionKey, entityPermissionKey);
    }

    private setPermissionsInfo(permissionsInfo: PermissionsInfo) {
        
        const { areaPermissionKey, entityPermissionKey } = permissionsInfo;

        if (areaPermissionKey && entityPermissionKey) {

            this.permissionsSubscription = this.getScreenEntityPermission$(areaPermissionKey, entityPermissionKey).subscribe(x => {
                if (x) {

                    if (x.canRead !== true && this.isRootComponent === true) {

                        this.goBack();

                        return;
                    }

                    const isInitial = !this.entityPermissions;

                    this.entityPermissions = x;

                    if (!isInitial) {
                        this.toggleControlsReadonly();
                        this.toggleButtonsReadonly();
                    }

                    this.onPermissionsUpdated();
                }
            });
        }
    }

    private toggleControlsReadonly() {
        setTimeout(() => {
            const wafFormControls = this.controls
                .filter(x => this.getControlForm(x.control) == this.ngForm.form && x.valueAccessor instanceof FormControlBase)
                .map(x => x.valueAccessor as FormControlBase<any>);

            if (this.entityPermissions) {
                wafFormControls.forEach(x => x.setReadonlyValueOverride(!this.canEditByControl(x)));
            }
        }, 0);
    }

    private toggleButtonsReadonly() {
        setTimeout(() => {
            this.buttons.forEach(x => {

                let makeDisplayed = false;

                if (x.specialAction) {
                    const sap: SpecialActionNode = this.specialActionPermissions[x.specialAction];

                    makeDisplayed = sap && sap.canExecute === true;
                }
                else if (x.permissions) {
                    const area = this.componentContext.permissionService.currentPermissions.entities[x.permissions.areaPermissionKey];
                    if (area) {
                        const entity = area[x.permissions.entityPermissionKey];
                        makeDisplayed = entity && entity.canEdit === true;
                    }
                }
                else {
                    makeDisplayed = this.canEditByControl(x);
                }

                x.toggleDisplay(makeDisplayed);
            });
        }, 0);
    }

    private canEditByControl(control: ActionButtonDirective | FormControlBase<any>) {
        const resultByControl = this.checkCanEditRuleByControl(control);
        if (resultByControl != null) return resultByControl;

        return this.canEdit;
    }

    private getControlForm(control: AbstractControl) {
        let parent = control.parent;
        if (parent == null)
            return null;
        while (parent.parent) {
            parent = parent.parent;
        }
        return parent;
    }
}
